import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  Address,
  Order,
  ShippingEstimate,
  ShippingOption,
  ShippingOrder,
  TypesOfShippings,
} from '@pedix-workspace/utils';

import { TranslocoDirective } from '@ngneat/transloco';
import { getOrderAddressHints } from '@pedix-workspace/shared-models';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { IconShippingComponent } from '@pedix-workspace/angular-ui-icons';
import { OrderShippingStatusComponent } from '../../order-shipping-status/order-shipping-status.component';
import { DatePipe, NgClass } from '@angular/common';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';

@Component({
    selector: 'pxw-order-details-shipping-option',
    templateUrl: './order-details-shipping-option.component.html',
    styleUrls: ['./order-details-shipping-option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoDirective,
        ButtonComponent,
        IconShippingComponent,
        OrderShippingStatusComponent,
        DatePipe,
        NgClass,
        AlertComponent,
    ]
})
export class OrderDetailsShippingOptionComponent {
  readonly shippingOption = input.required<ShippingOption>();
  readonly address = input.required<Address | null>();
  readonly shippingEstimate = input.required<ShippingEstimate | null>();
  readonly shippingOrder = input.required<ShippingOrder | null>();
  readonly tableNumber = input.required<string | null>();

  readonly displayShippingEstimate = input(false);
  readonly isShippingRequestInProgress = input(false);

  readonly confirmShipping = output();
  readonly createShipping = output();
  readonly cancelShipping = output();

  readonly isDelivery = computed(
    () =>
      this.shippingOption().type === TypesOfShippings.DELIVERY ||
      this.shippingOption().type === TypesOfShippings.PARCEL,
  );
  readonly shippingAddress = computed(() => {
    const address = this.address();

    if (!address) {
      return null;
    }
    return (address.addressDetails ? address.addressDetails.fullAddress : address.street) || '';
  });
  readonly addressLink = computed(() => {
    return this.address()?.addressDetails?.url;
  });
  readonly addressHints = computed((): string | null => {
    const address = this.address();

    if (!address) {
      return null;
    }
    return getOrderAddressHints(address, this.shippingOption().type);
  });

  readonly canCancelShipping = computed((): boolean => {
    const shippingOrder = this.shippingOrder();
    if (!shippingOrder) {
      return false;
    }

    if (shippingOrder.providerName === 'correoargentino') {
      return false;
    }

    const CANCELABLE_STATUSES = {
      pedidosya: ['CONFIRMED'],
      puniya: ['CONFIRMED'],
      rapiboy: ['CONFIRMED', 'IN_PROGRESS'],
      cabify: ['CONFIRMED'],
    } as Record<string, string[]>;

    return (
      CANCELABLE_STATUSES[shippingOrder.providerName]?.includes(shippingOrder.status ?? '') || false
    );
  });

  readonly shippingButtonText = computed(() => {
    const shippingEstimate = this.shippingEstimate();

    if (!shippingEstimate) {
      return 'Solicitar envío';
    }

    const isCorreoArgentino = shippingEstimate.providerName === 'correoargentino';
    const hasOfferId = !!shippingEstimate.offerId;

    if (isCorreoArgentino) {
      return hasOfferId ? 'Confirmar sincronización' : 'Sincronizar pedido';
    }

    return hasOfferId ? 'Confirmar envío' : 'Solicitar envío';
  });
}
